import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew, Storefront } from '@mui/icons-material';
import {
  SNUS_ACCENT,
  formatPrice,
  severityColor,
  stockLabel,
  visibilityRowLabel,
} from './snusProductUtils';

function DimensionBar({ label, value }) {
  if (value == null) return null;
  const n = Number(value);
  const color = n >= 75 ? 'success' : n >= 60 ? 'warning' : 'error';
  return (
    <Box sx={{ mb: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {value}
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, n))} color={color} />
    </Box>
  );
}

function RetailerCard({ retailer }) {
  const { retailer: name, pricing = {}, availability = {}, visibility = {} } = retailer;
  const stock = stockLabel(availability);
  const vis = visibilityRowLabel(visibility);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Storefront fontSize="small" color="action" />
        <Typography variant="subtitle2">{name || 'Unknown retailer'}</Typography>
      </Stack>

      <Stack spacing={1}>
        {(pricing['Actual price'] != null || pricing['Unit price'] != null) && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Pricing
            </Typography>
            <Typography variant="body2">
              {formatPrice(pricing.Currency, pricing['Actual price'])}
              {pricing['Unit price'] != null ? ` · unit ${formatPrice(pricing.Currency, pricing['Unit price'])}` : ''}
            </Typography>
            {pricing['Units (#)'] != null && (
              <Typography variant="caption" color="text.secondary">
                Pack size: {pricing['Units (#)']}
              </Typography>
            )}
          </Box>
        )}

        {stock && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Availability
            </Typography>
            <Chip
              size="small"
              label={stock}
              color={/out of stock|oos/i.test(stock) ? 'error' : 'success'}
              variant="outlined"
            />
            {availability['Days oos'] != null && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Days OOS: {availability['Days oos']}
              </Typography>
            )}
          </Box>
        )}

        {(vis || visibility['Promo type']) && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Visibility
            </Typography>
            <Typography variant="body2">{vis || visibility['Promo type']}</Typography>
            {visibility['Searched value'] && (
              <Typography variant="caption" color="text.secondary">
                {visibility['Searched value']}
              </Typography>
            )}
          </Box>
        )}

        {(pricing.Url || availability.Url || visibility.Url) && (
          <Link
            href={pricing.Url || availability.Url || visibility.Url}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            View on retailer <OpenInNew sx={{ fontSize: 14 }} />
          </Link>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * Shared product detail body for snus SKUs (popup + full page).
 */
export default function SnusProductDetailContent({ detail, loading, error, compact = false }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: compact ? 3 : 6 }}>
        <CircularProgress size={compact ? 28 : 40} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!detail) {
    return (
      <Typography color="text.secondary" variant="body2">
        Select a SKU to view product details.
      </Typography>
    );
  }

  const intel = detail.intelligence || {};
  const dims = intel.dimensions || {};
  const missing = Object.entries(detail.datasets || {})
    .filter(([, ok]) => !ok)
    .map(([k]) => k);

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 700, mb: 0.5 }}>
          {detail.title}
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
          {detail.brand && <Chip size="small" label={detail.brand} />}
          {detail.category && <Chip size="small" variant="outlined" label={detail.category} />}
          {detail.country && <Chip size="small" variant="outlined" label={detail.country} />}
          {detail.ean && <Chip size="small" variant="outlined" label={`EAN ${detail.ean}`} />}
          {intel.severity && (
            <Chip size="small" color={severityColor(intel.severity)} label={intel.severity} />
          )}
        </Stack>
        {(detail.internal_id || detail.snapshot_date) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {detail.internal_id ? `Internal id ${detail.internal_id}` : ''}
            {detail.internal_id && detail.snapshot_date ? ' · ' : ''}
            {detail.snapshot_date ? `Snapshot ${detail.snapshot_date}` : ''}
            {detail.week ? ` · ${detail.week}` : ''}
          </Typography>
        )}
      </Box>

      {intel.health_score != null && (
        <Paper variant="outlined" sx={{ p: 2, borderColor: SNUS_ACCENT, borderWidth: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Shelf health</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: SNUS_ACCENT }}>
              {intel.health_score}/100
            </Typography>
          </Stack>
          {intel.wow_change != null && (
            <Typography
              variant="caption"
              sx={{ color: intel.wow_change < 0 ? 'error.main' : 'success.main', display: 'block', mb: 1 }}
            >
              WoW {intel.wow_change > 0 ? '+' : ''}
              {intel.wow_change} pts
            </Typography>
          )}
          {intel.main_issue_display && intel.main_issue_display !== '—' && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Main issue: <strong>{intel.main_issue_display}</strong>
            </Typography>
          )}
          <DimensionBar label="Availability" value={dims.availability} />
          <DimensionBar label="Price" value={dims.price} />
          <DimensionBar label="Visibility" value={dims.visibility} />
          <DimensionBar label="Promotion" value={dims.promotion} />
          <DimensionBar label="Assortment" value={dims.assortment} />
        </Paper>
      )}

      {missing.length > 0 && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Missing datasets: {missing.join(', ')}. Download them for full scoring.
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          By retailer ({detail.retailer_count || detail.retailers?.length || 0})
        </Typography>
        <Stack spacing={1.5}>
          {(detail.retailers || []).map((r, i) => (
            <RetailerCard key={r.retailer || i} retailer={r} />
          ))}
        </Stack>
        {!detail.retailers?.length && (
          <Typography variant="body2" color="text.secondary">
            No retailer-level rows found for this SKU.
          </Typography>
        )}
      </Box>

      {!compact && detail.active_dimensions?.length > 0 && (
        <>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            Scored dimensions: {detail.active_dimensions.join(', ')}
          </Typography>
        </>
      )}
    </Stack>
  );
}
